/**
 * Regras do editor de modelos de checklist — sem React, para testar.
 *
 * O editor trabalha no MESMO formato do catálogo (`MaintenanceTemplateDefDto`) e
 * salva pela rota de publicação: conteúdo igual = nada muda; diferente = versão
 * nova. Por isso os códigos (seção e item) são preservados ao editar — é pelo
 * código que o histórico, a comparação de contagem e os indicadores juntam
 * respostas entre versões. Item novo ganha código a partir do rótulo.
 */
import type {
  MaintenanceAnswerTypeDto, MaintenanceNonConformityTriggerDto, MaintenanceTemplateDefDto,
  MaintenanceTemplateItemDefDto, MaintenanceTemplateSectionDefDto,
} from "@bi/types";

export function slugCode(text: string, max = 60): string {
  const s = text
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
    .slice(0, max).replace(/_+$/, "");
  return s.length >= 2 ? s : `x_${s || "item"}`.slice(0, max);
}

export function uniqueCode(base: string, taken: Iterable<string>): string {
  const usados = new Set(taken);
  if (!usados.has(base)) return base;
  for (let n = 2; ; n++) {
    const c = `${base.slice(0, 56)}_${n}`;
    if (!usados.has(c)) return c;
  }
}

export const ANSWER_TYPE_LABEL: Record<MaintenanceAnswerTypeDto, string> = {
  conformity: "Conforme / Não conforme / N.A.",
  yes_no: "Sim / Não",
  choice: "Escolha entre opções",
  quantity: "Quantidade (contagem)",
  number: "Número",
  text: "Texto livre",
  photo: "Foto",
  damage_map: "Mapa de avarias",
};

/** O que abre ocorrência, conforme o tipo de resposta — e a opção padrão. */
export const TRIGGERS_BY_TYPE: Record<MaintenanceAnswerTypeDto, Array<{ value: MaintenanceNonConformityTriggerDto; label: string }>> = {
  conformity: [{ value: "non_conforming", label: "Não conforme abre ocorrência" }, { value: "none", label: "Não abre ocorrência" }],
  yes_no: [{ value: "negative", label: "\"Não\" abre ocorrência" }, { value: "none", label: "Não abre ocorrência" }],
  choice: [{ value: "flagged_option", label: "Opções marcadas abrem ocorrência" }, { value: "none", label: "Não abre ocorrência" }],
  quantity: [{ value: "none", label: "Só compara com o checklist anterior" }, { value: "below_minimum", label: "Abaixo do mínimo abre ocorrência" }],
  number: [{ value: "none", label: "Não abre ocorrência" }],
  text: [{ value: "none", label: "Não abre ocorrência" }],
  photo: [{ value: "none", label: "Não abre ocorrência" }],
  damage_map: [{ value: "damage_marked", label: "Avaria marcada abre ocorrência" }, { value: "none", label: "Não abre ocorrência" }],
};

export const OUTRO = { value: "outro", label: "Outro (descrever)", requiresObservation: true } as const;

/** Item novo com os padrões do tipo. */
/** Código provisório de item/seção criado vazio; vira o slug do nome ao publicar. */
const PROVISORIO = /^novo(_\d+)?$/;

export function newItem(label: string, answerType: MaintenanceAnswerTypeDto, takenCodes: Iterable<string>): MaintenanceTemplateItemDefDto {
  const code = uniqueCode(label ? slugCode(label) : "novo", takenCodes);
  return withType({ code, label, answerType, required: true, criticality: "medium" }, answerType);
}

/**
 * Troca o tipo de resposta ajustando o que depende dele: gatilho padrão, opções
 * (só escolha), lista de problemas (só conformidade), mínimo (só quantidade).
 */
export function withType(item: MaintenanceTemplateItemDefDto, answerType: MaintenanceAnswerTypeDto): MaintenanceTemplateItemDefDto {
  const permitidos = TRIGGERS_BY_TYPE[answerType].map((t) => t.value);
  const next: MaintenanceTemplateItemDefDto = {
    ...item,
    answerType,
    nonConformityTrigger: item.nonConformityTrigger && permitidos.includes(item.nonConformityTrigger) && item.answerType === answerType
      ? item.nonConformityTrigger
      : permitidos[0],
  };
  if (answerType !== "choice") delete next.options;
  else if (!next.options?.length) next.options = [{ value: "ok", label: "OK" }, { value: "problema", label: "Com problema", nonConforming: true }];
  if (answerType !== "conformity") delete next.defectOptions;
  else if (!next.defectOptions?.length) next.defectOptions = [{ ...OUTRO }];
  if (answerType !== "quantity") delete next.minQuantity;
  return next;
}

export function newSection(title: string, takenCodes: Iterable<string>): MaintenanceTemplateSectionDefDto {
  return { code: uniqueCode(title ? slugCode(title) : "novo", takenCodes), title, items: [] };
}

export function move<T>(list: T[], index: number, delta: -1 | 1): T[] {
  const j = index + delta;
  if (j < 0 || j >= list.length) return list;
  const out = [...list];
  [out[index], out[j]] = [out[j]!, out[index]!];
  return out;
}

/** Problema novo digitado pelo gestor: código a partir do rótulo, "Outro" sempre por último. */
export function addDefect(item: MaintenanceTemplateItemDefDto, label: string): MaintenanceTemplateItemDefDto {
  const atuais = (item.defectOptions ?? []).filter((o) => o.value !== OUTRO.value);
  const novo = { value: uniqueCode(slugCode(label).slice(0, 40), [...atuais.map((o) => o.value), OUTRO.value]), label: label.trim() };
  return { ...item, defectOptions: [...atuais, novo, { ...OUTRO }] };
}

/** Aplica uma lista pronta (família), mantendo o "Outro" no fim. */
export function applyDefectFamily(item: MaintenanceTemplateItemDefDto, options: MaintenanceTemplateItemDefDto["defectOptions"]): MaintenanceTemplateItemDefDto {
  const semOutro = (options ?? []).filter((o) => o.value !== OUTRO.value);
  return { ...item, defectOptions: [...semOutro, { ...OUTRO }] };
}

/** O que impede salvar, na linguagem de quem edita (o servidor confere de novo). */
export function editorProblems(def: MaintenanceTemplateDefDto): string[] {
  const erros: string[] = [];
  if (def.name.trim().length < 3) erros.push("Dê um nome ao modelo.");
  if (def.sections.length === 0) erros.push("Inclua ao menos uma seção.");
  for (const s of def.sections) {
    if (s.title.trim().length < 2) erros.push("Há seção sem título.");
    if (s.items.length === 0) erros.push(`A seção "${s.title || "sem título"}" não tem itens.`);
    for (const i of s.items) {
      if (i.label.trim().length < 2) erros.push(`Há item sem nome na seção "${s.title}".`);
      if (i.answerType === "choice") {
        if ((i.options?.length ?? 0) < 2) erros.push(`"${i.label}": inclua ao menos duas opções.`);
        if (i.nonConformityTrigger === "flagged_option" && !i.options?.some((o) => o.nonConforming)) erros.push(`"${i.label}": marque qual opção abre ocorrência.`);
      }
      if (i.nonConformityTrigger === "below_minimum" && i.minQuantity == null) erros.push(`"${i.label}": informe a quantidade mínima.`);
    }
  }
  return [...new Set(erros)];
}

/** Limpa para enviar: textos aparados, ajuda vazia some. */
export function toPublishPayload(def: MaintenanceTemplateDefDto): MaintenanceTemplateDefDto {
  const txt = (v?: string) => (v && v.trim() ? v.trim() : undefined);
  // Códigos provisórios viram o slug do nome; os existentes ficam (o histórico junta por eles).
  const secoesUsadas = def.sections.map((s) => s.code).filter((c) => !PROVISORIO.test(c));
  const itensUsados = def.sections.flatMap((s) => s.items.map((i) => i.code)).filter((c) => !PROVISORIO.test(c));
  const codigo = (atual: string, nome: string, usados: string[]) => {
    if (!PROVISORIO.test(atual)) return atual;
    const c = uniqueCode(slugCode(nome), usados);
    usados.push(c);
    return c;
  };
  return {
    ...def,
    name: def.name.trim(),
    description: def.description.trim(),
    sections: def.sections.map((s) => ({
      code: codigo(s.code, s.title, secoesUsadas), title: s.title.trim(), ...(txt(s.helpText) ? { helpText: txt(s.helpText) } : {}),
      items: s.items.map((i) => {
        const out: MaintenanceTemplateItemDefDto = { ...i, code: codigo(i.code, i.label, itensUsados), label: i.label.trim() };
        if (txt(i.helpText)) out.helpText = txt(i.helpText); else delete out.helpText;
        return out;
      }),
    })),
  };
}
